import type {
  AgentChatSessionContract,
  AgentChatSessionSummaryContract,
} from '@nest-react/domain';
import { useEffect } from 'react';

import { useWorkspaceStore } from '~/stores/workspace/workspaceStore';

export const AGENT_CHAT_SESSION_CREATED_EVENT = 'agent-chat-session-created';
export const AGENT_CHAT_SESSION_UPDATED_EVENT = 'agent-chat-session-updated';
export const AGENT_CHAT_SESSION_DELETED_EVENT = 'agent-chat-session-deleted';

type WorkspaceScopedDetail = {
  workspaceId: string;
};

export type AgentChatSessionCreatedDetail = WorkspaceScopedDetail & {
  session: AgentChatSessionSummaryContract;
};

export type SessionPreviewMergeMode = 'preserve' | 'replace';

export type AgentChatSessionUpdatedDetail = WorkspaceScopedDetail & {
  session: AgentChatSessionSummaryContract;
  previewMode?: SessionPreviewMergeMode;
};

export type AgentChatSessionDeletedDetail = WorkspaceScopedDetail & {
  sessionId: string;
};

export const sessionContractToSummary = (
  session: AgentChatSessionContract
): AgentChatSessionSummaryContract => ({
  id: session.id,
  title: session.title,
  status: session.status,
  ...(session.activityStatus !== undefined
    ? { activityStatus: session.activityStatus }
    : {}),
  messageCount: session.messages.length,
  createdAt: session.createdAt,
  updatedAt: session.updatedAt,
});

export const cloneSessionSummary = (
  session: AgentChatSessionSummaryContract
): AgentChatSessionSummaryContract => ({ ...session });

export const cloneSessionSummaryList = (
  sessions: AgentChatSessionSummaryContract[]
): AgentChatSessionSummaryContract[] => sessions.map(cloneSessionSummary);

const areSessionSummariesEqual = (
  left: AgentChatSessionSummaryContract,
  right: AgentChatSessionSummaryContract
): boolean =>
  left.id === right.id &&
  left.title === right.title &&
  left.status === right.status &&
  left.activityStatus === right.activityStatus &&
  left.messageCount === right.messageCount &&
  left.previewUrl === right.previewUrl &&
  left.previewMediaType === right.previewMediaType &&
  left.previewImageUrl === right.previewImageUrl &&
  left.createdAt === right.createdAt &&
  left.updatedAt === right.updatedAt;

export const areSessionSummaryListsEqual = (
  left: AgentChatSessionSummaryContract[],
  right: AgentChatSessionSummaryContract[]
): boolean =>
  left.length === right.length &&
  left.every((item, index) => areSessionSummariesEqual(item, right[index]));

const mergeSessionSummary = (
  existing: AgentChatSessionSummaryContract | undefined,
  incoming: AgentChatSessionSummaryContract,
  previewMode: SessionPreviewMergeMode = 'replace'
): AgentChatSessionSummaryContract => {
  const merged: AgentChatSessionSummaryContract = {
    id: incoming.id,
    title: incoming.title ?? existing?.title,
    status: incoming.status ?? existing?.status ?? 'ACTIVE',
    activityStatus:
      incoming.activityStatus !== undefined
        ? incoming.activityStatus
        : existing?.activityStatus,
    messageCount: incoming.messageCount ?? existing?.messageCount ?? 0,
    createdAt: incoming.createdAt ?? existing?.createdAt ?? incoming.updatedAt,
    updatedAt: incoming.updatedAt,
  };

  if (previewMode === 'preserve') {
    merged.previewUrl = incoming.previewUrl ?? existing?.previewUrl;
    merged.previewMediaType =
      incoming.previewMediaType ?? existing?.previewMediaType;
    merged.previewImageUrl =
      incoming.previewImageUrl ?? existing?.previewImageUrl;
    return merged;
  }

  merged.previewUrl = incoming.previewUrl;
  merged.previewMediaType = incoming.previewMediaType;
  merged.previewImageUrl = incoming.previewImageUrl;
  return merged;
};

export const upsertSessionByRecency = (
  sessions: AgentChatSessionSummaryContract[],
  session: AgentChatSessionSummaryContract,
  limit?: number,
  previewMode: SessionPreviewMergeMode = 'replace'
): AgentChatSessionSummaryContract[] => {
  if (!session.id) {
    return sessions;
  }

  const existing = sessions.find(item => item.id === session.id);
  const merged = mergeSessionSummary(existing, session, previewMode);
  const next = [
    merged,
    ...sessions.filter(item => item.id !== session.id),
  ].sort(
    (left, right) =>
      new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime()
  );
  const bounded = limit ? next.slice(0, limit) : next;
  return areSessionSummaryListsEqual(bounded, sessions) ? sessions : bounded;
};

export const removeSessionFromList = (
  sessions: AgentChatSessionSummaryContract[],
  sessionId: string
): AgentChatSessionSummaryContract[] =>
  sessions.filter(session => session.id !== sessionId);

const dispatchWorkspaceEvent = <TDetail extends WorkspaceScopedDetail>(
  name: string,
  detail: TDetail
): void => {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent(name, { detail }));
};

export const notifyAgentChatSessionCreated = (
  workspaceId: string,
  session: AgentChatSessionSummaryContract
): void => {
  dispatchWorkspaceEvent<AgentChatSessionCreatedDetail>(
    AGENT_CHAT_SESSION_CREATED_EVENT,
    { workspaceId, session }
  );
};

export const notifyAgentChatSessionUpdated = (
  workspaceId: string,
  session: AgentChatSessionSummaryContract,
  options?: { previewMode?: SessionPreviewMergeMode }
): void => {
  dispatchWorkspaceEvent<AgentChatSessionUpdatedDetail>(
    AGENT_CHAT_SESSION_UPDATED_EVENT,
    { workspaceId, session, previewMode: options?.previewMode }
  );
};

export const notifyAgentChatSessionDeleted = (
  workspaceId: string,
  sessionId: string
): void => {
  dispatchWorkspaceEvent<AgentChatSessionDeletedDetail>(
    AGENT_CHAT_SESSION_DELETED_EVENT,
    { workspaceId, sessionId }
  );
};

let sessionListListenersRegistered = false;

const ensureAgentChatSessionListEventListeners = (): void => {
  if (sessionListListenersRegistered || typeof window === 'undefined') {
    return;
  }
  sessionListListenersRegistered = true;

  const handleCreated = (event: Event): void => {
    if (!(event instanceof CustomEvent)) return;
    const detail = event.detail as AgentChatSessionCreatedDetail | undefined;
    if (!detail?.workspaceId) return;
    useWorkspaceStore
      .getState()
      .upsertWorkspaceAgentSession(detail.workspaceId, detail.session, {
        previewMode: 'replace',
      });
  };

  const handleUpdated = (event: Event): void => {
    if (!(event instanceof CustomEvent)) return;
    const detail = event.detail as AgentChatSessionUpdatedDetail | undefined;
    if (!detail?.workspaceId) return;
    useWorkspaceStore
      .getState()
      .upsertWorkspaceAgentSession(detail.workspaceId, detail.session, {
        previewMode: detail.previewMode ?? 'replace',
      });
  };

  const handleDeleted = (event: Event): void => {
    if (!(event instanceof CustomEvent)) return;
    const detail = event.detail as AgentChatSessionDeletedDetail | undefined;
    if (!detail?.workspaceId) return;
    useWorkspaceStore
      .getState()
      .removeWorkspaceAgentSession(detail.workspaceId, detail.sessionId);
  };

  window.addEventListener(AGENT_CHAT_SESSION_CREATED_EVENT, handleCreated);
  window.addEventListener(AGENT_CHAT_SESSION_UPDATED_EVENT, handleUpdated);
  window.addEventListener(AGENT_CHAT_SESSION_DELETED_EVENT, handleDeleted);
};

export const useAgentChatSessionListEvents = (_workspaceId: string): void => {
  useEffect(() => {
    ensureAgentChatSessionListEventListeners();
  }, []);
};
